import React, { useState, useEffect, useContext } from "react";
import axiosInstance from "api/axiosInstance";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import { useTheme, alpha } from "@mui/material/styles";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";

// ✅ 橘色主題色已併入全域 Theme，此處保留變數供其他組件參考
const ORANGE_PRIMARY = COLORS.main;

export default function ExampleFormulaDialog({
  open,
  onClose,
  onApply,
  fieldType,
  initialSelected = [], // ✅ 接收初始勾選狀態
  demoMode = false,        // DEMO 頁：不打需登入的 API，改用本地範例
  localExamples = null,    // demoMode 時的本地範例陣列 [{language,fieldType,name,formula}]
}) {
  const theme = useTheme();
  const [exampleList, setExampleList] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selected, setSelected] = useState([]);

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  
  useEffect(() => {
    loadLanguageFile("ExampleFormulaDialog", "strategy/ExampleFormulaDialog/ExampleFormulaDialog_lang");
  }, [language, loadLanguageFile]);

  const LANGUAGE = componentLanguageData.ExampleFormulaDialog || { Main: {}, Error: {} };

  // ✅ 抓取範例資料
  useEffect(() => {
    if (!fieldType) return;

    // DEMO 頁：公開、打不到需登入的 API → 用本地範例(依 fieldType + 語系篩選)，形狀補上 id 對齊正式版
    if (demoMode) {
      const lang = (language || "").toUpperCase();
      const norm = lang.includes("EN") ? "EN" : lang.includes("CN") ? "CN" : "ZH";
      const list = (localExamples || [])
        .filter((e) => e.fieldType === fieldType && (e.language === norm || e.language === "EN"))
        .map((e, i) => ({ id: `${fieldType}-${i}-${e.formula}`, name: e.name, formula: e.formula }));
      // 若該語系沒有資料，退回該 fieldType 全部(避免空清單)
      setExampleList(list.length ? list : (localExamples || [])
        .filter((e) => e.fieldType === fieldType)
        .map((e, i) => ({ id: `${fieldType}-${i}-${e.formula}`, name: e.name, formula: e.formula })));
      return;
    }

    const fetchExamples = async () => {
      try {
        const res = await axiosInstance.get(`/strategy/logic-examples?fieldType=${fieldType}`);
        const data = Array.isArray(res.data) ? res.data : [];
        setExampleList(data);
      } catch (err) {
        console.error(LANGUAGE.Error.Content1, err);
        setExampleList([]);
      }
    };

    fetchExamples();
  }, [fieldType, demoMode, localExamples, language]);

  // ✅ 當視窗開啟時，同步初始狀態
  useEffect(() => {
    if (open) {
      setSelected(initialSelected || []);
    }
  }, [open, initialSelected]);

  // ✅ 清除勾選邏輯
  const handleClearAll = () => {
    setSelected([]);
  };

  // ✅ 切換勾選狀態
  const toggleSelection = (formula) => {
    setSelected((prev) =>
      prev.includes(formula) ? prev.filter((f) => f !== formula) : [...prev, formula]
    );
  };

  // ✅ 篩選後資料
  const filteredList = exampleList.filter((ex) =>
    ex.name.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const handleApply = () => {
    if (selected.length === 0) {
      onClose();
      return;
    }
    onApply(selected);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: "0 12px 40px rgba(0,0,0,0.12)",
          overflow: "hidden",
          maxWidth: "680px !important", // ✅ 增加寬度
        }
      }}
    >
      {/* --- Header --- */}
      <DialogTitle sx={{ 
        p: 2.5,
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
      }}>
        <Typography variant="h6" fontWeight={700} color="text.primary">
          {LANGUAGE.Main.Content1}
        </Typography>
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {selected.length > 0 && (
            <Button 
              size="small" 
              variant="outlined"
              onClick={handleClearAll}
              sx={{ 
                mr: 1,
                borderRadius: 2,
                color: ORANGE_PRIMARY,
                borderColor: ORANGE_PRIMARY,
                fontWeight: 700,
                fontSize: "0.8rem",
                px: 1.5,
                "&:hover": {
                  borderColor: ORANGE_PRIMARY,
                  bgcolor: alpha(ORANGE_PRIMARY, 0.05),
                }
              }}
            >
              {LANGUAGE.Main.Content7 || "清除全部"}
            </Button>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ 
              color: theme.palette.grey[400],
              "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.1) }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, px: 3, pb: 1, display: "flex", flexDirection: "column" }}>
        {/* --- Search Bar --- */}
        <TextField
          fullWidth
          placeholder={LANGUAGE.Main.Content2}
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          sx={{ 
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: alpha(theme.palette.grey[100], 0.4),
              "& fieldset": { borderColor: "transparent" },
              "&:hover fieldset": { borderColor: "transparent" },
              "&.Mui-focused": {
                bgcolor: COLORS.white,
                "& fieldset": { borderColor: alpha(theme.palette.grey[300], 0.5) },
              }
            },
            "& .MuiInputBase-input": { py: 1.5 }
          }}
          InputProps={{
            endAdornment: (
              <Box sx={{ bgcolor: alpha(theme.palette.grey[100], 0.8), borderRadius: 2, p: 0.5, display: "flex" }}>
                <SearchIcon sx={{ color: theme.palette.grey[600], fontSize: 20 }} />
              </Box>
            ),
          }}
        />

        {/* --- Formula List (Cards) --- */}
        <Box sx={{ 
          flex: 1, 
          maxHeight: 450, 
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { borderRadius: 3, bgcolor: alpha(theme.palette.grey[400], 0.2) }
        }}>
          {filteredList.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {LANGUAGE.Main.Content3}
              </Typography>
            </Box>
          ) : (
            filteredList.map((item) => {
              const isActive = selected.includes(item.formula);
              return (
                <Box
                  key={item.id}
                  onClick={() => toggleSelection(item.formula)}
                  sx={{
                    mb: 2,
                    p: 2.5,
                    borderRadius: 3,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    position: "relative",
                    bgcolor: isActive ? alpha(ORANGE_PRIMARY, 0.03) : alpha(theme.palette.grey[50], 0.8),
                    border: "2px solid",
                    borderColor: isActive ? ORANGE_PRIMARY : alpha(theme.palette.grey[100], 0.5), // ✅ 未選中時顏色更淡
                    "&:hover": {
                      bgcolor: isActive ? alpha(ORANGE_PRIMARY, 0.05) : alpha(theme.palette.grey[100], 0.8),
                      transform: "translateY(-2px)",
                      boxShadow: isActive ? "0 4px 12px rgba(241,107,42,0.1)" : "0 4px 12px rgba(0,0,0,0.04)"
                    }
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <Checkbox
                      checked={isActive}
                      color="primary" // ✅ 現在全域主題已改為橘色，直接使用 primary
                      sx={{ p: 0, mt: 0.2 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={700} sx={{ mb: 1, color: "text.primary" }}>
                        {item.name}
                      </Typography>
                      <Box sx={{ 
                        p: 1.5, 
                        bgcolor: COLORS.white, 
                        borderRadius: 2, 
                        border: "1px solid",
                        borderColor: alpha(theme.palette.grey[200], 0.5)
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: "monospace", 
                            color: "text.primary",
                            fontSize: "0.85rem",
                            wordBreak: "break-all"
                          }}
                        >
                          {item.formula}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>

      {/* --- Footer --- */}
      <DialogActions sx={{ p: 3, pt: 1, justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {LANGUAGE.Main.Content8 || "已選擇"} <span style={{ color: ORANGE_PRIMARY, fontWeight: 700, fontSize: "1.1rem" }}>{selected.length}</span> {LANGUAGE.Main.Content9 || "個公式"}
        </Typography>
        
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button 
            onClick={onClose}
            sx={{ 
              px: 3, 
              py: 1, 
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 600,
              color: theme.palette.grey[700],
              bgcolor: alpha(theme.palette.grey[200], 0.6),
              "&:hover": { bgcolor: alpha(theme.palette.grey[300], 0.8) }
            }}
          >
            {LANGUAGE.Main.Content4}
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={selected.length === 0}
            sx={{ 
              px: 3, 
              py: 1, 
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: ORANGE_PRIMARY,
              color: COLORS.white,
              boxShadow: "0 4px 14px " + alpha(ORANGE_PRIMARY, 0.4),
              "&:hover": {
                bgcolor: "#d85a1e",
                boxShadow: "0 6px 20px " + alpha(ORANGE_PRIMARY, 0.4),
              },
              "&.Mui-disabled": {
                bgcolor: alpha(theme.palette.grey[400], 0.3),
                color: alpha(theme.palette.grey[600], 0.5),
                boxShadow: "none"
              }
            }}
          >
            {LANGUAGE.Main.Content5}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

